import { createSelector } from 'reselect';
import { propOr } from 'ramda';

import { PHONE_RA_TYPE } from '../constants/requiredActions.constants';
import { getRecipientState } from '../../../store/users/users.selectors';
import { getCountries, getCountriesMap } from '../../../store/countries/countries.selectors';
import { getIsCommonDataLoaded } from '../../../store/root.selectors';
import { getDetectedCountry, getIsLoading, getIsSkipped } from '../../../store/geoIpCountry/geoIpCountry.selectors';
import { GiftActionFlow } from '../../../store/types/GiftActionFlow.types';

import {
  getCountryId,
  getFlowHashId,
  getFlowRecipient,
  getProspects,
  getShipping,
} from './giftFlow/giftFlow.selectors';
import {
  getCanBeExchanged,
  getGiftHashId,
  getGiftProductCountryId,
  getIsCode,
  getIsGiftInfoLoaded,
} from './data/data.selectors';
import { getIsActivate } from './statuses/statuses.selectors';
import {
  getIsAddressChanged,
  getIsAddressVerified,
  getIsValidated,
  getShippingAddress,
} from './shipping/shipping.selectors';
import { ShippingAddressFlow } from './shipping/shipping.types';

export const getRecipient = createSelector(getRecipientState, getFlowRecipient, (initialRecipient, flowRecipient) =>
  flowRecipient && flowRecipient.email !== '' ? flowRecipient : initialRecipient,
);

export const getRecipientEmail = createSelector(
  getIsCommonDataLoaded,
  getRecipientState,
  getFlowRecipient,
  getGiftHashId,
  getFlowHashId,
  (isLoaded, initialRecipient, flowRecipient, hashId, flowHashId) => {
    const isTheSameGift = hashId === flowHashId;
    if (isTheSameGift && flowRecipient && flowRecipient.email !== '') {
      return flowRecipient.email;
    }

    const initialRecipientEmail = initialRecipient?.email ?? '';
    return !isTheSameGift && isLoaded ? initialRecipientEmail : '';
  },
);

export const getProductCountry = createSelector(
  getGiftProductCountryId,
  getCountriesMap,
  (countryId, countriesMap) => countriesMap[countryId],
);

export const getCanDeclineGift = createSelector(
  getIsActivate,
  getIsCode,
  (isActivate, isCode) => !isActivate && !isCode,
);

export const getInitialShippingValues = createSelector(
  getShipping,
  getShippingAddress,
  getProspects,
  getRecipientState,
  getCountryId,
  (flowShipping, shipping, prospects, recipient, countryId) => {
    const savedAddress = flowShipping?.address || shipping?.address;
    return {
      address: '',
      city: '',
      state: '',
      zipcode: '',
      ...savedAddress,
      country: savedAddress?.country || countryId,
      phone: savedAddress?.phone || propOr('', PHONE_RA_TYPE, prospects),
    };
  },
);

export const getShippingAddressFlow = createSelector(
  getIsAddressChanged,
  getIsAddressVerified,
  getIsValidated,
  (hasAddressChanges, isAddressVerified, isValidated) =>
    isValidated && (!isAddressVerified || (isAddressVerified && hasAddressChanges))
      ? ShippingAddressFlow.addressSelectionForm
      : ShippingAddressFlow.shippingForm,
);

export const getIsGiftDataLoaded = createSelector(
  getIsCommonDataLoaded,
  getIsGiftInfoLoaded,
  (isCommonLoaded, isGiftInfoLoaded) => isCommonLoaded && isGiftInfoLoaded,
);

export const getGiftActionFlow = createSelector(
  getProductCountry,
  getDetectedCountry,
  getCountries,
  getCanBeExchanged,
  getIsLoading,
  getIsSkipped,
  (productCountry, detectedCountry, countries, canBeExchanged, isGeoIpLoading, isSkipped) => {
    const hasCountries = !!productCountry && !!detectedCountry;
    const isGiftCountryEqualsDetected = hasCountries && productCountry?.code === detectedCountry?.code;

    if (isGeoIpLoading) {
      return GiftActionFlow.loading;
    }

    if (!productCountry || !detectedCountry || isGiftCountryEqualsDetected || isSkipped) {
      return GiftActionFlow.giftCountry;
    }

    const countriesCodes = countries.map(country => country.code);

    return productCountry.code !== detectedCountry.code &&
      countriesCodes.includes(detectedCountry.code) &&
      canBeExchanged
      ? GiftActionFlow.campaignCountry
      : GiftActionFlow.wrongCountry;
  },
);
